import authAxios from 'src/modules/shared/axios/authAxios';
import AuthCurrentTenant from 'src/modules/auth/authCurrentTenant';
import moment from "moment";
import { tenantSubdomain } from '../tenant/tenantSubdomain';
import TenantService from '../../modules/tenant/tenantService';
import XLSX from 'xlsx';

export default class EventService {
  static async update(id, data) {
    const body = {
      id,
      data,
    };

    if(data.eventHour) {
      data.eventHour = moment(new Date(data.eventHour)).format('HH:mm');
    }

    const tenantId = AuthCurrentTenant.get();

    const response = await authAxios.put(
      `/tenant/${tenantId}/event/${id}`,
      body,
    );

    return response.data;
  }

  static async destroyAll(ids) {
    const params = {
      ids,
    };

    const tenantId = AuthCurrentTenant.get();

    const response = await authAxios.delete(
      `/tenant/${tenantId}/event`,
      {
        params,
      },
    );

    return response.data;
  }

  static async create(data) {
    const body = {
      data,
    };

    if(data.eventHour) {
      data.eventHour = moment(new Date(data.eventHour)).format('HH:mm');
    }

    const tenantId = AuthCurrentTenant.get();

    const response = await authAxios.post(
      `/tenant/${tenantId}/event`,
      body,
    );

    return response.data;
  }

  static async import(values, importHash, fileName) {
    const body = {
      data: values,
      importHash,
      fileName
    };

    const tenantId = AuthCurrentTenant.get();
   
    const response = await authAxios.post(
      `/tenant/${tenantId}/event/import`,
      body,
    );

    return response.data;
  }
  

  static preprocessBeforeImport(content) {

    const isProbablyDate = (value: string) => {
      const mmddyyPattern = /^(0?[1-9]|1[0-2])\/(0?[1-9]|[12][0-9]|3[01])\/\d{2}$/;
      return mmddyyPattern.test(value.trim());
    };

    const modifiedContent = content
      .split('\n')      
      .filter((line) => !line.match(/^[,\s]*$/)) //delete empty lines.
      .map((line) => {
        if (line.trim() === '') return line;
        const columns = line.split(',');
        if (columns.length > 0) {
          columns[0] = `="${columns[0]}"`;   //event from
          columns[1] = `="${columns[1]}"`;   //event to
          columns[2] = `="${columns[2]}"`;   //event name (=title) 
        }
        const updatedColumns = columns.map((col) => {
          return isProbablyDate(col) ? `="${col.trim()}"` : col;
        });
        return updatedColumns.join(',');
      })
      .join('\n');  
    console.log(modifiedContent);

    const sheet = XLSX.read(modifiedContent, { type: "string", raw: false }).Sheets["Sheet1"];
    const parsedData = XLSX.utils.sheet_to_json(sheet, { defval: "" });     
    
    const groupedData: any[] = Object.values(
      parsedData.reduce<Record<string, any>>((acc, row: any) => {
        const columns = Object.keys(row);         
        const keyColumns = columns.slice(0, 17); //important assumption.         
        const key = keyColumns.map(col => row[col]).join("|");      
        if (!acc[key]) {
          acc[key] = { ...row, eventSchedule: [] };
        }       
        if (row[columns[17]]) { //event name
          const eventScheduleData = {
            name: row[columns[17]],
            eventFrom: row[columns[18]],
            eventHour: row[columns[19]]
          }; 
          acc[key].eventSchedule.push(eventScheduleData);         
        }    
        return acc;
      }, {})
    ).map(row => ({
      ...row,
      eventSchedule: JSON.stringify(row.eventSchedule) 
    }));      

    const columnNames = Object.keys(groupedData[0]);
    const first17 = Array.from({ length: 17 }, (_, index) => index); 
    const lastColumn = [columnNames.length - 1]; 
    const after17 = Array.from(
      { length: columnNames.length - 20 }, 
      (_, index) => index + 20
    ).filter(index => !lastColumn.includes(index));
    const selectedPositions = [...first17, ...lastColumn, ...after17];     
    const filteredData = groupedData.map(row =>
      Object.fromEntries(selectedPositions.map(i => [columnNames[i], row[columnNames[i]]]))
    );
    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const csvData = XLSX.utils.sheet_to_csv(worksheet);
       
    return csvData;
  }  

  static preDownloadTemplate(labels) {  
    const newItems = ["Name","EventFrom","EventHour"];
    const newLabels = labels.flatMap(item => 
      item === "EventSchedule" ? newItems : [item]
    );  
    return newLabels;
  }

  static preprocessBeforeImportExcel(content) {

    const excelTimeToString = (value: unknown): string => {
      if (typeof value !== 'number') return '';    
      const totalMinutes = Math.round(value * 24 * 60);
      const hours = Math.floor(totalMinutes / 60).toString().padStart(2, '0');
      const minutes = (totalMinutes % 60).toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    };

    const excelSerialToDate = (excelSerialDate: number): Date => {
      const baseDate = new Date(Date.UTC(1899, 11, 30)); // Excel base in UTC
      return new Date(baseDate.getTime() + excelSerialDate * 24 * 60 * 60 * 1000);
    };

    var contentModified = content;
    const insertValues: { [key: number]: any } = {
      4: '',        
      14: '',       
      15: '',      
      16: 'false', 
      17: '[]', 
      18: '', 
      20: '',       
      21: '',       
      22: ''
    };
    (contentModified as any[][]).forEach(row => {
      Object.keys(insertValues).forEach(key => {
        const position = parseInt(key, 10);
        const value = insertValues[position];
        row.splice(position, 0, value);
      });
    });   
    contentModified = contentModified.map(row => {
        const newRow = [...row];
        //newRow[0] = newRow[0]?excelSerialToDate(newRow[0]):null;   
        //newRow[1] = newRow[1]?excelSerialToDate(newRow[1]):null;   
        newRow[5] = newRow[5]?excelTimeToString(newRow[5]):null;       
        return newRow;
      });   
    return contentModified;  
  }  

  static async find(id) {
    const tenantId = AuthCurrentTenant.get();

    const response = await authAxios.get(
      `/tenant/${tenantId}/event/${id}`,
    );

    return response.data;
  }

  static async list(filter, orderBy, limit, offset) {
    const params = {
      filter,
      orderBy,
      limit,
      offset,
    };

    const tenantId = AuthCurrentTenant.get();

    const response = await authAxios.get(
      `/tenant/${tenantId}/event`,
      {
        params,
      },
    );

    return response.data;
  }

  static async listAutocomplete(query, limit) {
    const params = {
      query,
      limit,
    };

    const tenantId = AuthCurrentTenant.get();

    const response = await authAxios.get(
      `/tenant/${tenantId}/event/autocomplete`,
      {
        params,
      },
    );

    return response.data;
  }

  static async publishAll(ids) {
    const params = {
      ids,
    };

    const tenantId = AuthCurrentTenant.get();

    const response = await authAxios.put(
      `/tenant/${tenantId}/eventpublish`,
      {
        params,
      },
    );

    return response.data;
  }

  static async unpublishAll(ids) {
    const params = {
      ids,
    };

    const tenantId = AuthCurrentTenant.get();

    const response = await authAxios.put(
      `/tenant/${tenantId}/eventunpublish`,
      {
        params,
      },
    );

    return response.data;
  }

  static async publicScheduleAll(ids) {
    const params = {
      ids,
    };

    const tenantId = AuthCurrentTenant.get();

    const response = await authAxios.put(
      `/tenant/${tenantId}/eventpublicschedule`,
      {
        params,
      },
    );

    return response.data;
  }

  static async notPublicScheduleAll(ids) {
    const params = {
      ids,
    };

    const tenantId = AuthCurrentTenant.get();

    const response = await authAxios.put(
      `/tenant/${tenantId}/eventnotpublicschedule`,
      {
        params,
      },
    );

    return response.data;
  }

  static async cloneAll(ids) {
    const params = {
      ids,
    };

    const tenantId = AuthCurrentTenant.get();

    const response = await authAxios.put(
      `/tenant/${tenantId}/eventclone`,
      {
        params,
      },
    );

    return response.data;
  }
  
  static async promoteAll(ids, targetVisibility) {
    const params = {
      ids,
      targetVisibility,
    };

    const tenantId = AuthCurrentTenant.get();

    const response = await authAxios.put(
      `/tenant/${tenantId}/eventpromote`,
      {
        params,
      },
    );

    return response.data;
  }

  static async demoteAll(ids, targetVisibility) {
    const params = {
      ids,
      targetVisibility,
    };

    const tenantId = AuthCurrentTenant.get();

    const response = await authAxios.put(
      `/tenant/${tenantId}/eventdemote`,
      {
        params,
      },
    );

    return response.data;
  }

  static async findInvitedTenants(id) {
    const tenantId = AuthCurrentTenant.get();
    const response = await authAxios.get(
      `/tenant/${tenantId}/event/${id}/invited-tenants`,
    );
    return response.data;
  }

  static async updateInvitedTenants(id, tenantIds) {
    const tenantId = AuthCurrentTenant.get();
    const response = await authAxios.put(
      `/tenant/${tenantId}/event/${id}/invited-tenants`,
      { tenantIds },
    );
    return response.data;
  }

  static async findCopiedTenants(id) {
    const tenantId = AuthCurrentTenant.get();
    const response = await authAxios.get(
      `/tenant/${tenantId}/event/${id}/copied-tenants`,
    );
    return response.data;
  }

  static async copyToTenants(id, tenantIds) {
    const tenantId = AuthCurrentTenant.get();
    const response = await authAxios.post(
      `/tenant/${tenantId}/event/${id}/copied-tenants`,
      { tenantIds },
    );
    return response.data;
  }

  static async findCopyableTenants() {
    const tenantId = AuthCurrentTenant.get();
    const response = await authAxios.get(
      `/tenant/${tenantId}/eventcopytenants`,
    );
    return response.data;
  }

  static async copyAllToTenants(ids, tenantIds) {
    const tenantId = AuthCurrentTenant.get();
    const response = await authAxios.post(
      `/tenant/${tenantId}/eventcopytenants`,
      { ids, tenantIds },
    );
    return response.data;
  }

  static async eventsUpcoming(
    viewMode,
    searchFilter,
    categoryFilter,
    tagFilter,
    countryFilter,
    placeFilter,
    classFilter,
    monthFilter,
    showPastEventsFilter,
    isAuthenticated,
    userViewMode = false,
  ) {
    var tenantId = null;
    if (isAuthenticated) {
      tenantId = AuthCurrentTenant.get();
    } else {
      const tenantUrl = tenantSubdomain.fromLocationHref();
      if (
        tenantSubdomain.isEnabled &&
        !tenantSubdomain.isRootDomain
      ) {
        const currentTenant = await TenantService.findByUrl(
          tenantUrl,
        );
        tenantId = currentTenant.id;
      }
    }
    const params = {
      viewMode,
      searchFilter,
      categoryFilter,
      tagFilter,
      countryFilter,
      placeFilter,
      classFilter,
      monthFilter,
      showPastEventsFilter,
      tenantId,
      userViewMode,
    };
    if (!isAuthenticated) {
      const response = await authAxios.get(
        `/event/upcoming-events`,
        {
          params,
        },
      );
      return response.data;
    } else {
      const tenantId = AuthCurrentTenant.get();
      const response = await authAxios.get(
        `/tenant/${tenantId}/event/upcoming-events`,
        {
          params,
        },
      );
      return response.data;
    }
  }

  static async eventUpcomingDetail(id, isAuthenticated, userViewMode = false) {
    var tenantId = null;
    if (isAuthenticated) {
      tenantId = AuthCurrentTenant.get();
    } else {
      const tenantUrl = tenantSubdomain.fromLocationHref();
      if (
        tenantSubdomain.isEnabled &&
        !tenantSubdomain.isRootDomain
      ) {
        const currentTenant = await TenantService.findByUrl(
          tenantUrl,
        );
        tenantId = currentTenant.id;
      }
    }
    const params = {
      tenantId,
      userViewMode,
    };
    if (!isAuthenticated) {
      const response = await authAxios.get(
        `/event/upcoming-event-detail/${id}`,
        {
          params,
        },
      );
      return response.data;
    } else {
      const tenantId = AuthCurrentTenant.get();
      const response = await authAxios.get(
        `/tenant/${tenantId}/event/upcoming-event-detail/${id}`,
        {
          params,
        },
      );
      return response.data;
    }
  }
  
  static async eventsUpcomingContactCreate(
    firstname,
    lastname,
    email,
    country,
    countryState,
    zipCode,
    mobile,
    weightUnit,
    weight,   
    gender,
    categories,    
    positions
  ) {
    var tenantId = null;    
    const tenantUrl = tenantSubdomain.fromLocationHref();
    if (
      tenantSubdomain.isEnabled &&
      !tenantSubdomain.isRootDomain
    ) {
      const currentTenant = await TenantService.findByUrl(
          tenantUrl,
      );
      tenantId = currentTenant.id;
    }    
    const body = {
      source:"detail_page",
      firstname,
      lastname,
      email,
      country,
      countryState,
      zipCode,
      mobile,
      weightUnit,
      weight,      
      gender,
      categories,
      positions,     
      tenantId      
    };   
    const response = await authAxios.post(
      `/event/upcoming-event-contact-create`,
      body,
    );
    return response.data;    
  }
  
  static async eventsUpcomingCategoriesPositions(
  ) {
    const params = {
    };
    const response = await authAxios.get(
        `/event/upcoming-events-categories-positions`,
        {
          params,
        },
    );
    return response.data;
  }

  static async eventWeather(id) {
    const response = await authAxios.get(`/event/upcoming-event-weather/${id}`);
    return response.data;
  }
}
